<template>
  <section class="analyze-container">
    <header class="analyze-header">
      <h1 class="title">
        Welcome to Teamwrkr
      </h1>
      <p class="body">Build your AI-powered Profile, a dynamic profile that grows with your experience and aspirations.</p>
      <p class="body">It will take you just a few minutes to create your foundation and see how your skills align with key technical roles and career paths.</p>
      <p class="body">Enter your name and email to generate your Profile and unlock community access when it’s live.</p>
    </header>

    <div class="analyze-content">

      <form @submit.prevent="handleSubmit" class="analyze-intro-form">
        <b-field label="First Name" required>
          <b-input v-model="firstName" placeholder="Enter your first name" required></b-input>
        </b-field>
        <b-field label="Last Name" required>
          <b-input v-model="lastName" placeholder="Enter your last name" required></b-input>
        </b-field>
        <b-field label="Email Address" required>
          <b-input v-model="email" type="email" placeholder="Enter your email address" required></b-input>
        </b-field>
        <div class="button-group">
          <b-button
            type="is-primary"
            native-type="submit"
            :disabled="!firstName || !lastName || !email || loading"
            :loading="loading"
          >
            Build My Profile!
          </b-button>
        </div>
      </form>
      <p class="body">Your information is kept secure and used only to create your Profile and anonymized insights that help improve the platform.</p>
    </div>

    <!-- Email In Use Modal -->
    <b-modal
      v-model="showEmailModal"
      :can-cancel="['escape', 'outside', 'button']"
      :width="400"
      trap-focus
    >
      <div class="modal-content">
        <h2 class="modal-title">
          <i class="mdi mdi-alert-circle-outline"></i>
          Email Already In Use
        </h2>
        <p class="modal-desc">
          This email address is already registered. Please recover it by clicking below.
        </p>
        <div class="modal-actions">
          <router-link
            class="button is-primary"
            :to="{ path: '/verify' }"
            @click="goToSignIn"
          >
            Go to Account Recovery
          </router-link>
          <b-button type="is-text" @click="showEmailModal = false">
            Cancel
          </b-button>
        </div>
      </div>
    </b-modal>
  </section>
</template>

<script>
import { useAnalyzeStore } from '../stores/analyzeStore'
import { useUserStore } from '@/stores/userStore'
import { savePendingTeamLink, teamConfirmationRoute, takePendingAccessLink, accessConfirmationRoute } from '@/utils/pendingTeamLink'

export default {
  name: 'WelcomePage',
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      roster: this.$route.query.team || this.$route.query.roster || null,
      // Signed team invite token from the manager's link (spec-reporting-hierarchy). Without a
      // valid one, signing up from the link sends the manager a join request instead.
      invite: this.$route.query.invite || null,
      companyHandle: this.$route.params.handle || null,
      checkCompanyHandle: null,
      loading: false,
      showEmailModal: false
    };
  },
  async created() {
    const token = this.$cookies.get('teamwrkr-token')

    if (token) {
      const userStore = useUserStore()

      try {
        await userStore.fetchUser(token)

        if (userStore.user?._id) {
          console.log('User already logged in, redirecting to home page.');
          if (this.roster) {
            console.log('team query param found, redirecting to /team-confirmation');
            this.$router.push(teamConfirmationRoute({ team: this.roster, invite: this.invite }));
            return;
          }

          // spec-admin-gated-company-access: an access-request link parked while signed out.
          const access = takePendingAccessLink();
          if (access) {
            this.$router.push(accessConfirmationRoute(access));
            return;
          }
          this.$router.push('/');
          return
        }
      } catch (error) {
        // Allow Welcome to render if the cookie is stale or invalid.
      }
    }
    console.log('team query param:', this.roster);
    if (this.companyHandle) {
      console.log('company handle:', this.companyHandle)
      this.checkCompanyHandle = await this.checkCompany();
      console.log('Here', this.checkCompanyHandle)

    }
    this.analyzeStore = useAnalyzeStore()
  },
  methods: {
    // Existing account: sign in by email first. Park the team link (sessionStorage, never the
    // email) so Verify.vue can finish it after /confirm-identity.
    goToSignIn() {
      savePendingTeamLink({ team: this.roster, invite: this.invite });
      this.showEmailModal = false;
    },
    async checkCompany() {
      const response = await this.axios.get(
        `${process.env.VUE_APP_API_URL}/companies/check-handle/${this.companyHandle}`,
      );
      console.log('company res', response.data.exists);
      return response.data.exists
    },
    async handleSubmit() {
      if (!this.firstName || !this.lastName || !this.email) return;
      this.loading = true;
      try {
        console.log('Submitting:', this.firstName, this.lastName, this.email);

        const response = await this.axios.post(
          `${process.env.VUE_APP_API_URL}/users/register`,
          {
            firstName: this.firstName,
            lastName: this.lastName,
            email: this.email,
            roster: this.roster,
            invite: this.invite,
            company: this.checkCompanyHandle ? this.checkCompanyHandle : null,
          },
        );
        console.log('response.data', response.data);

        // if the user already exists, show modal
        if (response.data.exists) {
          this.showEmailModal = true;
          this.loading = false;
          return;
        } else {
          // If registration is successful, store user and redirect to analyze intro
          this.analyzeStore.setUser(response.data.user)
          this.analyzeStore.setOnboardingToken(response.data.onboardingToken)
          // spec-admin-gated-company-access: signup never places anyone in an existing company;
          // the tenant owner / an admin approves first.
          if (response.data.access === 'pending_verification') {
            this.$buefy.toast.open({
              message: 'Once you verify your email, your company admin will be asked to approve your access. You can build your profile in the meantime.',
              type: 'is-info',
              duration: 8000,
            });
          } else if (response.data.access === 'requested') {
            this.$buefy.toast.open({
              message: 'Your company admin has been asked to approve your access. You can build your profile in the meantime.',
              type: 'is-info',
              duration: 8000,
            });
          } else if (response.data.access === 'needs_teamwrkr') {
            this.$buefy.toast.open({
              message: 'Your company has no admin who can approve new people yet. Teamwrkr will help you get access.',
              type: 'is-warning',
              duration: 8000,
            });
          }
          console.log('ANALYZE STORE AFTER REGISTRATION:', this.analyzeStore.user);
          this.$router.push({ name: 'AnalyzeIntro' });
        }
      } catch (err) {
        this.$buefy.toast.open({
          message: 'There was an error. Please try again.',
          type: 'is-danger'
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.analyze-container {
  background: $color-surface-alt;
  border-radius: 1.2rem;
  box-shadow: 0 4px 32px rgba(170, 118, 214, 0.07);
  padding: 2.5rem 2rem 2rem 2rem;
  max-width: 700px;
  margin: 2.5rem auto;
  color: $color-text;
}

.analyze-header {
  margin-bottom: 2rem;
  text-align: center;

  .title {
    font-size: 2rem;
    font-weight: 700;
    color: $color-primary;
    margin-bottom: 1.7rem;
  }
  .subtitle {
    font-size: 1.15rem;
    color: $color-text-muted;
    margin-bottom: 0.7rem;
    font-style: italic;
  }
  .body {
    font-size: 1.05rem;
    color: $color-text;
    margin-bottom: 0.4rem;
  }
}

.analyze-content {
  background: $color-surface;
  border-radius: 1rem;
  box-shadow: 0 2px 12px rgba(170, 118, 214, 0.05);
  padding: 2rem 1.2rem 1.5rem 1.2rem;
  margin-bottom: 2rem;
}

.b-field {
  margin-bottom: 1.5rem;
}

.button-group {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.2rem;
  margin-bottom: 1.2rem;
}

.b-button {
  border-radius: 0.7rem;
  font-weight: 700;
  font-size: 1.08rem;
  border: none;
  box-shadow: 0 2px 8px rgba(170, 118, 214, 0.07);
  &.is-primary {
    background: $color-primary;
    color: $color-surface;
    transition: background 0.18s, color 0.18s;
    &:hover, &:focus {
      background: $color-accent;
      color: $color-surface;
    }
  }
  &.is-secondary {
    background: $color-surface-soft;
    color: $color-primary;
    &:hover, &:focus {
      background: $color-border;
      color: $color-text;
    }
  }
}

.modal-content {
  padding: 1.5rem 1rem;
  background: $color-surface;
  border-radius: 0.75rem;
  color: $color-text;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.modal-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: $color-primary;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.2rem;
  i {
    color: $color-warning;
    font-size: 1.7rem;
  }
}

.modal-desc {
  font-size: 1.05rem;
  color: $color-text-muted;
  margin-bottom: 0.5rem;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
  justify-content: flex-end;
}

@media screen and (max-width: 700px) {
  .analyze-container {
    padding: 1.2rem 0.5rem;
    max-width: 99vw;
  }
  .analyze-content {
    padding: 1rem 0.3rem;
  }
}
</style>
